import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import { useEffect } from 'react';

export default function SomersetWestPage() {
  useEffect(() => {
    document.title = "Somerset West Painters Cape Town | Professional Painting Services | Quality Residential & Commercial";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Professional painting services for Somerset West properties. Expert interior & exterior painting, roof painting, and waterproofing for homes and businesses in Somerset West, Cape Town.');
    }
    
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'Somerset West painters, residential painting, commercial painting, Somerset West homes, Cape Town painters');
    }
    
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/somerset-west`);
    }
    
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Somerset West Painters - Professional Painting Services",
      "description": "Professional painting services for Somerset West properties with quality finishes and expert craftsmanship.",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/somerset-west`,
      "mainEntity": {
        "@type": "LocalBusiness",
        "name": "Cape Town Painters - Somerset West",
        "description": "Professional painting services for Somerset West residential and commercial properties.",
        "areaServed": "Somerset West, Cape Town",
        "serviceType": "Professional Painting Services"
      }
    });
    document.head.appendChild(script);
    
    return () => {
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript && existingScript.textContent?.includes('Somerset West')) {
        existingScript.remove();
      }
    };
  }, []);

  const services = [
    {
      icon: 'ri-home-line',
      title: 'Residential Painting',
      description: 'Complete interior and exterior painting services for Somerset West homes. From modern estates to family homes, we deliver quality finishes that enhance your property value.',
      price: 'From R8,000',
      features: ['Interior painting', 'Exterior painting', 'Color consultation', 'Quality materials', 'Professional finish']
    },
    {
      icon: 'ri-building-line',
      title: 'Commercial Painting',
      description: 'Professional painting solutions for Somerset West businesses, offices, and commercial properties. Minimal disruption with flexible scheduling to suit your business needs.',
      price: 'From R12,000',
      features: ['Office painting', 'Retail spaces', 'Industrial units', 'After-hours service', 'Quick turnaround']
    },
    {
      icon: 'ri-home-gear-line',
      title: 'Roof & Waterproofing',
      description: 'Specialized roof painting and waterproofing services to protect your Somerset West property from the elements. Expert solutions for long-lasting protection.',
      price: 'From R6,000',
      features: ['Roof painting', 'Waterproofing', 'Leak repairs', 'Protective coatings', 'Weather resistance']
    }
  ];

  const features = [
    {
      title: 'Quality Materials',
      description: 'We use premium paints from leading brands including Dulux and Plascon, ensuring durability and beautiful finishes that stand the test of time in Somerset West\'s climate.'
    },
    {
      title: 'Expert Craftsmen',
      description: 'Our experienced team brings years of expertise to every Somerset West project, delivering professional results with attention to detail and superior workmanship.'
    },
    {
      title: 'Comprehensive Service',
      description: 'From initial consultation to final inspection, we provide complete painting solutions including surface preparation, repairs, and quality assurance for your peace of mind.'
    },
    {
      title: 'Reliable & Punctual',
      description: 'We respect your time and property. Our team arrives on schedule, works efficiently, and maintains clean work areas throughout your Somerset West painting project.'
    }
  ];

  const paintingProcess = [
    {
      step: 1,
      title: 'Free Consultation',
      description: 'Comprehensive property assessment with color consultation and detailed quotation for your Somerset West property. We discuss your vision and provide expert recommendations.'
    },
    {
      step: 2,
      title: 'Surface Preparation',
      description: 'Thorough preparation including cleaning, repairs, and priming to ensure optimal paint adhesion and long-lasting results for your Somerset West home or business.'
    },
    {
      step: 3,
      title: 'Professional Application',
      description: 'Expert application using quality materials and proven techniques. Our skilled painters ensure even coverage and flawless finishes throughout your property.'
    },
    {
      step: 4,
      title: 'Final Inspection',
      description: 'Detailed quality check and walkthrough to ensure complete satisfaction. We address any concerns and ensure your Somerset West property looks perfect.'
    }
  ];

  const testimonials = [
    {
      name: 'David Thompson',
      property: 'Somerset West Estate',
      rating: 5,
      text: 'Excellent service from start to finish. The team was professional, punctual, and the quality of work exceeded our expectations. Our home looks absolutely stunning and we couldn\'t be happier with the results.',
      image: 'https://public.readdy.ai/ai/img_res/49ab5a4c348885c8efcde5331dd11db3.jpg'
    },
    {
      name: 'Sarah Mitchell',
      property: 'Family Home',
      rating: 5,
      text: 'Outstanding work on our family home. The painters were respectful, clean, and delivered beautiful results. Highly recommend for anyone in Somerset West looking for quality painting services at fair prices.',
      image: 'https://readdy.ai/api/search-image?query=Elegant%20woman%20homeowner%20at%20Somerset%20West%20family%20home%2C%20professionally%20painted%20interior%2C%20satisfied%20client%20portrait%2C%20modern%20residential%20property&width=80&height=80&seq=somersetwest2&orientation=squarish'
    },
    {
      name: 'Michael van der Berg',
      property: 'Commercial Property',
      rating: 5,
      text: 'Professional service for our commercial property. They worked efficiently, minimized disruption to our business, and delivered excellent results. Great value for money and highly professional throughout.',
      image: 'https://readdy.ai/api/search-image?query=Professional%20business%20owner%20at%20Somerset%20West%20commercial%20property%2C%20newly%20painted%20building%20exterior%2C%20satisfied%20customer%2C%20quality%20commercial%20painting&width=80&height=80&seq=somersetwest3&orientation=squarish'
    }
  ];

  const projects = [
    {
      title: 'Modern Estate Home',
      description: 'Complete exterior and interior painting for a modern Somerset West estate. Premium finishes with contemporary color scheme enhancing the property\'s architectural features.',
      image: 'https://readdy.ai/api/search-image?query=Modern%20estate%20home%20in%20Somerset%20West%20with%20Helderberg%20mountain%20backdrop%2C%20freshly%20painted%20white%20exterior%2C%20contemporary%20architecture%2C%20professional%20painting%20work%2C%20quality%20finish&width=400&height=300&seq=somersetwest4&orientation=landscape'
    },
    {
      title: 'Family Residence',
      description: 'Full interior renovation with fresh colors and quality finishes. Transformed living spaces creating a warm and inviting atmosphere for this Somerset West family home.',
      image: 'https://readdy.ai/api/search-image?query=Somerset%20West%20family%20residence%20with%20Helderberg%20views%2C%20newly%20painted%20exterior%2C%20residential%20property%2C%20professional%20painting%20finish%2C%20quality%20workmanship&width=400&height=300&seq=somersetwest5&orientation=landscape'
    },
    {
      title: 'Commercial Office',
      description: 'Professional office painting with modern color scheme. Completed efficiently with minimal business disruption, creating a fresh professional environment for staff and clients.',
      image: 'https://readdy.ai/api/search-image?query=Somerset%20West%20commercial%20office%20building%2C%20professional%20exterior%20painting%2C%20business%20property%2C%20modern%20commercial%20development%2C%20quality%20finish%20work&width=400&height=300&seq=somersetwest6&orientation=landscape'
    },
    {
      title: 'Roof Restoration',
      description: 'Complete roof painting and waterproofing project. Enhanced protection and curb appeal for this Somerset West property with long-lasting weather-resistant coatings.',
      image: 'https://readdy.ai/api/search-image?query=Somerset%20West%20home%20with%20Helderberg%20mountain%20view%2C%20professionally%20painted%20roof%2C%20residential%20property%2C%20roof%20restoration%20work%2C%20quality%20painting&width=400&height=300&seq=somersetwest7&orientation=landscape'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative bg-cover bg-center bg-no-repeat min-h-screen flex items-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('https://readdy.ai/api/search-image?query=Professional%20painters%20working%20on%20residential%20property%20in%20Somerset%20West%20with%20Helderberg%20mountain%20backdrop%2C%20painting%20equipment%2C%20scaffolding%2C%20quality%20home%2C%20sunny%20day%2C%20Cape%20Town%20suburb&width=1920&height=1080&seq=somersetwest_hero&orientation=landscape')`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Professional Painters Somerset West
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed">
              Quality painting services for Somerset West homes and businesses. Expert craftsmanship and reliable service with over 15 years of experience in the area.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/services"
                className="bg-white text-black px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors text-center whitespace-nowrap cursor-pointer border border-gray-200"
              >
                Our Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* About Somerset West Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Trusted Painting Services in Somerset West
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Somerset West is a beautiful town nestled at the foot of the Helderberg Mountains, known for its stunning natural beauty, wine estates, and quality residential properties. This growing area combines suburban comfort with easy access to Cape Town's amenities.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                Our painting team has been serving Somerset West for over 15 years, building a reputation for quality workmanship, reliability, and excellent customer service. We understand the local climate and use appropriate materials to ensure your paint job lasts.
              </p>
              <p className="text-lg text-gray-600">
                From family homes to commercial properties, we deliver professional painting services that enhance your property's appearance and protect it from the elements. Our commitment to quality and customer satisfaction has made us a trusted choice in Somerset West.
              </p>
            </div>
            <div>
              <img
                src="https://readdy.ai/api/search-image?query=Somerset%20West%20residential%20area%20with%20Helderberg%20mountain%20backdrop%2C%20quality%20homes%2C%20suburban%20setting%2C%20beautiful%20Cape%20Town%20suburb%2C%20residential%20properties&width=600&height=400&seq=somersetwest8&orientation=landscape"
                alt="Somerset West Properties"
                className="w-full h-96 object-cover object-top rounded-xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Somerset West Painting Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive painting solutions for residential and commercial properties throughout Somerset West and surrounding areas.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                  <i className={`${service.icon} text-black text-2xl`}></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-4">{service.description}</p>
                <div className="text-yellow-600 font-semibold text-lg mb-4">{service.price}</div>
                <ul className="text-sm text-gray-600 space-y-1">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center">
                      <i className="ri-check-line text-yellow-600 mr-2"></i>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose Our Somerset West Painters
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Professional service, quality materials, and expert craftsmanship for every Somerset West painting project.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Painting Process Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Painting Process
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A systematic approach ensuring quality results and complete satisfaction for every Somerset West project.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {paintingProcess.map((process, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-yellow-400 text-black rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold" style={{backgroundColor: '#ffff00'}}>
                  {process.step}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{process.title}</h3>
                <p className="text-gray-600">{process.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Recent Projects */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Recent Somerset West Projects
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Showcasing our recent painting projects across Somerset West residential and commercial properties.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {projects.map((project, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-64 object-cover object-top"
                />
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{project.title}</h3>
                  <p className="text-gray-600">{project.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Client Reviews
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Testimonials from satisfied Somerset West property owners who have experienced our professional painting services.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-lg">
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400 text-lg"></i>
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic">"{testimonial.text}"</p>
                <div className="flex items-center">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover mr-4"
                  />
                  <div>
                    <h4 className="font-semibold text-gray-900">{testimonial.name}</h4>
                    <p className="text-gray-600 text-sm">{testimonial.property}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-yellow-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-black mb-6">
            Ready to Transform Your Somerset West Property?
          </h2>
          <p className="text-xl text-black/80 mb-8 max-w-3xl mx-auto">
            Contact Cape Town's trusted painting professionals for Somerset West. Get a free quote and experience quality workmanship that enhances your property.
          </p>
          <Link
            to="/contact"
            className="inline-block bg-teal-600 text-white px-8 py-4 rounded-lg text-xl font-bold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
          >
            Get Free Quote
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
